import { ActionIcon, Checkbox, ColorInput, NumberInput, PasswordInput, PinInput, SegmentedControl, Switch, TextInput, Tooltip } from "@mantine/core";
import { DatePicker, DatePickerInput, TimePicker } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { ComponentProps, forwardRef, lazy, memo, Suspense } from "react";
import { Control, FieldPath, FieldPathValue, FieldValues, useController, UseControllerProps } from "react-hook-form";
import { IMaskInput } from "react-imask";

import { withController } from "../_HOCs";
import { InputSelect } from "./Select";
import { InputTransferList } from "./TransferList";
const LazyRichTextEditor = lazy(() => import("../RichTextEditor").then((m) => ({ default: m.RichTextEditor })));
const LazyRichTextEditorControlled = lazy(() => import("../RichTextEditorControlled").then((m) => ({ default: m.RichTextEditorControlled })));

//! DATE
const InputDate = memo(
  forwardRef<HTMLButtonElement, ComponentProps<typeof DatePickerInput>>((props, ref) => {
    return <DatePickerInput maxLevel="month" /* defaultLevel="month" */ {...props} ref={ref} />;
  })
);
InputDate.displayName = "InputDate";

//! DATE RANGE (DatePicker)
type InputDateRangeProps = Omit<ComponentProps<typeof DatePicker>, "renderDay" | "getDayProps"> & {
  highlightMatcher?: (date: Date) => boolean;
  highlightTooltip?: (date: Date) => string;
  highlightStyle?: React.CSSProperties;
  renderDay?: (date: Date) => React.ReactNode;
  getDayProps?: (date: Date) => Record<string, unknown>;
  /** Novo: múltiplos grupos de destaque */
  highlightGroups?: Array<{
    matcher: (date: Date) => boolean;
    tooltip?: (date: Date) => string | undefined;
    style?: React.CSSProperties;
    /** Variant predefinida de estilo */
    variant?: "danger" | "warning" | "info" | "success" | "primary";
    /** Prioridade (menor número = maior prioridade) */
    priority?: number;
    /** Label para legenda */
    label?: string;
    /** Indicador custom (ícone / mini badge) */
    marker?: React.ReactNode;
  }>;
  /** Mostrar múltiplos indicadores quando vários grupos casam */
  allowMultipleHighlights?: boolean;
  /** Exibe legenda baseada nos grupos */
  showLegend?: boolean;
  legendPosition?: "bottom" | "right";
  /** Callback com informações de renderização por dia */
  onDayRenderInfo?: (info: { date: Date; groups: Array<{ label?: string; variant?: string }> }) => void;
  /** Combina tooltips de múltiplos grupos em uma única tooltip */
  aggregateTooltip?: boolean;
  /** Permite clicar na legenda para ativar/desativar grupos */
  interactiveLegend?: boolean;
};

const defaultHighlightStyle: React.CSSProperties = {
  backgroundColor: "var(--mantine-color-orange-filled)",
  color: "var(--mantine-color-white)",
  textDecoration: "line-through"
};

const InputDateRange = memo(
  forwardRef<HTMLDivElement, InputDateRangeProps>((props, ref) => {
    const {
      highlightMatcher,
      highlightTooltip,
      highlightStyle = defaultHighlightStyle,
      highlightGroups,
      renderDay: _renderDay,
      getDayProps: _getDayProps,
      type,
      allowMultipleHighlights = false,
      showLegend = false,
      onDayRenderInfo,
      aggregateTooltip = false,
      interactiveLegend = false,
      ...rest
    } = props;

    // Paleta de variants
    const variantStyles: Record<string, React.CSSProperties> = {
      danger: {
        backgroundColor: "var(--mantine-color-red-filled)",
        color: "var(--mantine-color-white)",
        textDecoration: "line-through"
      },
      warning: {
        backgroundColor: "var(--mantine-color-orange-filled)",
        color: "var(--mantine-color-white)",
        textDecoration: "line-through"
      },
      info: {
        backgroundColor: "var(--mantine-color-blue-filled)",
        color: "var(--mantine-color-white)"
      },
      success: {
        backgroundColor: "var(--mantine-color-green-filled)",
        color: "var(--mantine-color-white)"
      },
      primary: {
        backgroundColor: "var(--mantine-primary-color-filled)",
        color: "var(--mantine-color-white)"
      }
    };

    // Cache de resultados de matchers para otimizar múltiplos renders
    const matcherCache = new Map<string, Array<{ group: NonNullable<typeof highlightGroups>[number] }>>();

    // Estado de grupos ativos quando interactiveLegend
    const activeGroupsRef = { current: new Set<number>() };
    if (interactiveLegend && activeGroupsRef.current.size === 0 && highlightGroups) {
      highlightGroups.forEach((_, idx) => activeGroupsRef.current.add(idx));
    }

    const resolveMatches = (date: Date) => {
      const key = dayjs(date).format("YYYY-MM-DD");
      if (matcherCache.has(key)) return matcherCache.get(key)!;

      const matches: Array<{ group: NonNullable<typeof highlightGroups>[number] }> = [];

      if (highlightGroups && highlightGroups.length > 0) {
        highlightGroups.forEach((g, idx) => {
          if (interactiveLegend && !activeGroupsRef.current.has(idx)) return;
          try {
            if (g.matcher(date)) {
              matches.push({ group: g });
            }
          } catch {
            // ignore matcher error
          }
        });
        matches.sort((a, b) => (a.group.priority ?? 100) - (b.group.priority ?? 100));
      } else if (highlightMatcher && highlightMatcher(date)) {
        // Compatibilidade modo antigo: cria grupo sintético mínimo
        matches.push({
          group: {
            matcher: highlightMatcher,
            tooltip: highlightTooltip,
            style: highlightStyle
          } as NonNullable<typeof highlightGroups>[number]
        });
      }

      matcherCache.set(key, matches);
      return matches;
    };

    return (
      <>
        <DatePicker
          type={type || "range"}
          {...rest}
          ref={ref}
          renderDay={(rawDate) => {
            const date = dayjs(rawDate as unknown as Date | string).toDate();
            const matches = resolveMatches(date);
            const primary = matches[0];
            const dayNumber = date.getDate();
            let tooltip: string | undefined = undefined;
            if (aggregateTooltip && matches.length > 1) {
              const lines = matches.map((m) => (m.group.tooltip ? m.group.tooltip(date) : m.group.label)).filter(Boolean) as string[];
              tooltip = lines.length ? lines.join("\n") : undefined;
            } else {
              tooltip = primary?.group.tooltip ? primary.group.tooltip(date) : undefined;
            }

            if (onDayRenderInfo) {
              onDayRenderInfo({
                date,
                groups: matches.map((m) => ({ label: m.group.label, variant: m.group.variant }))
              });
            }

            let multiIndicators: React.ReactNode = null;
            if (allowMultipleHighlights && matches.length > 1) {
              multiIndicators = (
                <div style={{ display: "flex", gap: 2, marginTop: 2, justifyContent: "center" }}>
                  {matches.slice(0, 3).map((m, idx) => {
                    const style = m.group.style || (m.group.variant ? variantStyles[m.group.variant] : undefined) || highlightStyle;
                    if (m.group.marker) {
                      return (
                        <span key={idx} title={m.group.label} style={{ lineHeight: 1, display: "flex" }}>
                          {m.group.marker}
                        </span>
                      );
                    }
                    return (
                      <span
                        key={idx}
                        title={m.group.label}
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 4,
                          background: style?.backgroundColor || style?.color || "var(--mantine-color-gray-5)",
                          display: "inline-block"
                        }}
                      />
                    );
                  })}
                  {matches.length > 3 && <span style={{ fontSize: 8 }}>+{matches.length - 3}</span>}
                </div>
              );
            }

            const base = (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div>{dayNumber}</div>
                {multiIndicators}
              </div>
            );

            const wrapped = tooltip ? (
              <Tooltip label={tooltip} offset={10}>
                <div>{base}</div>
              </Tooltip>
            ) : (
              base
            );
            return _renderDay ? _renderDay(date) : wrapped;
          }}
          getDayProps={(rawDate) => {
            const date = dayjs(rawDate as unknown as Date | string).toDate();
            const matches = resolveMatches(date);
            const primary = matches[0];
            const style = primary?.group.style || (primary?.group.variant ? variantStyles[primary.group.variant] : undefined) || primary?.group.style;
            const original = _getDayProps ? _getDayProps(date) : {};

            // Filtrar propriedades que não são compatíveis com DataAttributes
            const filteredOriginal: Record<string, string | number | boolean | undefined> = {};
            if (original && typeof original === "object") {
              for (const [key, value] of Object.entries(original)) {
                if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value === undefined) {
                  filteredOriginal[key] = value;
                }
              }
            }

            return style ? { style, ...filteredOriginal } : filteredOriginal;
          }}
        />
        {showLegend && (highlightGroups?.length ?? 0) > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              marginTop: 8,
              alignItems: "center"
            }}
          >
            {highlightGroups!
              .map((g, i) => ({ g, i }))
              .filter(({ g }) => g.label)
              .sort((a, b) => (a.g.priority ?? 100) - (b.g.priority ?? 100))
              .map(({ g, i }) => {
                const style = g.style || (g.variant ? variantStyles[g.variant] : undefined) || highlightStyle;
                const isActive = !interactiveLegend || activeGroupsRef.current.has(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      if (!interactiveLegend) return;
                      if (activeGroupsRef.current.has(i)) {
                        activeGroupsRef.current.delete(i);
                      } else {
                        activeGroupsRef.current.add(i);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      cursor: interactiveLegend ? "pointer" : "default",
                      opacity: isActive ? 1 : 0.4,
                      background: "transparent",
                      border: "none",
                      padding: 0
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 4,
                        background: style?.backgroundColor || style?.color || "var(--mantine-color-gray-5)"
                      }}
                    />
                    <span>{g.label}</span>
                  </button>
                );
              })}
          </div>
        )}
      </>
    );
  })
);
InputDateRange.displayName = "InputDateRange";

//! COLOR
const InputColor = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof ColorInput>>((props, ref) => {
    const swatches = [
      "#4D4D4D",
      "#999999",
      "#FFFFFF",
      "#F44E3B",
      "#FE9200",
      "#FCDC00",
      "#DBDF00",
      "#A4DD00",
      "#68CCCA",
      "#73D8FF",
      "#AEA1FF",
      "#FDA1FF",
      "#333333",
      "#808080",
      "#cccccc",
      "#D33115",
      "#E27300",
      "#FCC400",
      "#B0BC00",
      "#68BC00",
      "#16A5A5",
      "#009CE0",
      "#7B64FF",
      "#FA28FF",
      "#000000",
      "#666666",
      "#B3B3B3",
      "#9F0500",
      "#C45100",
      "#FB9E00",
      "#808900",
      "#194D33",
      "#0C797D",
      "#0062B1",
      "#653294",
      "#AB149E"
    ];

    return <ColorInput swatches={swatches} {...props} ref={ref} />;
  })
);
InputColor.displayName = "InputColor";

//! TEXT
type InputTextProps = {
  maskProps?: ComponentProps<typeof IMaskInput>;
} & ComponentProps<typeof TextInput>;
const InputText = memo(
  forwardRef<HTMLInputElement, InputTextProps>(({ maskProps, ...props }, ref) => {
    const [opened, { toggle }] = useDisclosure(false);

    // Only for password type: show/hide password
    const isPassword = props.type === "password";
    const inputType = isPassword && opened ? "text" : props.type;

    const rightSection = isPassword
      ? (props.rightSection ?? (
          <ActionIcon onClick={toggle} variant="transparent" size="sm" tabIndex={-1}>
            {opened ? <EyeSlashIcon weight="fill" /> : <EyeIcon weight="fill" />}
          </ActionIcon>
        ))
      : props.rightSection;

    if (maskProps?.mask) {
      const { mask, ...maskRest } = maskProps;
      const replacedMask = mask.toString().replaceAll("9", "0");

      return (
        <>
          {/* <TextInput
            renderRoot={(inputProps) => <ReactInputMask maskChar={maskChar} {...maskRest} {...inputProps} />}
            {...props}
            type={inputType}
            rightSection={rightSection}
            ref={ref}
          /> */}

          {/* @ts-expect-error - Não é necessário se preocupar com esse erro de tipagem referente a prop mask */}
          <TextInput component={IMaskInput} mask={replacedMask} {...maskRest} {...props} ref={ref} />
        </>
      );
    }

    return <TextInput {...props} type={inputType} rightSection={rightSection} ref={ref} />;
  })
);
InputText.displayName = "InputText";

const InputPassword = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof PasswordInput>>((props, ref) => {
    return <PasswordInput {...props} ref={ref} />;
  })
);
InputPassword.displayName = "InputPassword";

//! CHECKBOX
const InputCheckbox = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof Checkbox>>(({ value, ...props }, ref) => {
    return <Checkbox {...props} checked={Boolean(value)} ref={ref} />;
  })
);

//! SEGMENTED CONTROL
const InputSegmentedControl = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof SegmentedControl>>((props, ref) => {
    return <SegmentedControl {...props} ref={ref} />;
  })
);
InputSegmentedControl.displayName = "InputSegmentedControl";

//! NUMBER
const InputNumber = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof NumberInput>>((props, ref) => {
    return <NumberInput {...props} ref={ref} />;
  })
);
InputNumber.displayName = "InputNumber";

//! PIN
const InputPin = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof PinInput>>((props, ref) => {
    return <PinInput {...props} ref={ref} />;
  })
);
InputPin.displayName = "InputPin";

//! TIME
const InputTime = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof TimePicker>>((props, ref) => {
    return <TimePicker {...props} presets={["01:00", "02:00", "03:00", "04:00", "05:00", "06:00"]} ref={ref} withDropdown />;
  })
);
InputTime.displayName = "InputTime";

//! SWITCH
const InputSwitch = memo(
  forwardRef<HTMLInputElement, ComponentProps<typeof Switch>>(({ value, ...props }, ref) => {
    return <Switch checked={Boolean(value)} {...props} ref={ref} />;
  })
);
InputSwitch.displayName = "InputSwitch";

/**
 * TransferList component integrated with react-hook-form.
 *
 * @example
 * // Basic usage with react-hook-form
 * import { useForm } from 'react-hook-form';
 * import { Input } from '@/components/@custom/Input';
 *
 * interface FormData {
 *   drivers: TransferListData<Driver>;
 * }
 *
 * function MyForm() {
 *   const { control } = useForm<FormData>({
 *     defaultValue: {
 *       drivers: {
 *         left: [{ id: 1, name: 'João' }, { id: 2, name: 'Maria' }],
 *         right: []
 *       }
 *     }
 *   });
 *
 *   return (
 *     <Input.TransferList
 *       name="drivers"
 *       control={control}
 *       rules={{ required: 'Campo obrigatório' }}
 *       getItemLabel={(item) => item.name}
 *       getItemValue={(item) => item.id}
 *     />
 *   );
 * }
 *
 * @example
 * // With loading state
 * <Input.TransferList
 *   name="users"
 *   control={control}
 *   loading={isLoadingUsers}
 *   loaderProps={{ color: 'blue', variant: 'dots' }}
 *   getItemLabel={(item) => item.name}
 *   getItemValue={(item) => item.id}
 * />
 *
 * @example
 * // With validation
 * <Input.TransferList
 *   name="items"
 *   control={control}
 *   rules={{
 *     required: 'Selecione pelo menos um item',
 *     validate: (value) => value.right.length > 0 || 'Mova pelo menos um item para a direita'
 *   }}
 * />
 */

// Helper type to extract the item type from TransferListData
type ExtractItemType<T> = T extends { left: Array<infer U>; right: Array<infer U> } ? U : never;

function ControlledInputTransferList<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>, TItem = ExtractItemType<FieldPathValue<TFieldValues, TName>>>({
  name,
  control,
  defaultValue,
  disabled,
  rules,
  shouldUnregister,
  ...props
}: Omit<ComponentProps<typeof InputTransferList<TItem>>, "value" | "onChange" | "onBlur" | "name" | "error"> &
  Omit<UseControllerProps<TFieldValues, TName>, "control"> & { control: Control<TFieldValues> }) {
  const {
    field: { disabled: disabledState, name: nameState, ...controllerRest },
    fieldState: { error: fieldError }
  } = useController({ name, control, defaultValue, disabled, rules, shouldUnregister });

  return <InputTransferList<TItem> disabled={disabledState} name={nameState} error={fieldError?.message} {...controllerRest} {...props} />;
}

export const Input = {
  /**
   * TransferList component controlled by react-hook-form.
   * Use this version when working with forms.
   *
   * For uncontrolled usage, use Input.Uncontrolled.TransferList instead.
   */
  TransferList: ControlledInputTransferList,
  Select: withController(InputSelect),
  Number: withController(InputNumber),
  Color: withController(InputColor),
  Date: withController(InputDate),
  DateRange: withController(InputDateRange),
  Text: withController(InputText),
  Pin: withController(InputPin),
  Time: withController(InputTime),
  Switch: withController(InputSwitch),
  Password: withController(InputPassword),
  Checkbox: withController(InputCheckbox),
  SegmentedControl: withController(InputSegmentedControl),
  RichText: (props: any) => (
    <Suspense fallback={null}>
      <LazyRichTextEditorControlled {...props} />
    </Suspense>
  ),
  DateRangeHelpers: {
    createHighlightGroupFromRanges: (options: {
      ranges: Array<{ start: string; end: string }>; // YYYY-MM-DD
      label?: string;
      variant?: "danger" | "warning" | "info" | "success" | "primary";
      style?: React.CSSProperties;
      priority?: number;
      marker?: React.ReactNode;
      tooltipPrefix?: string;
    }) => {
      const { ranges, label, variant, style, priority, marker, tooltipPrefix } = options;
      const normalized: Array<{ start: string; end: string }> = ranges.map((r) => ({ start: r.start, end: r.end }));
      return {
        matcher: (d: Date) => {
          const iso = dayjs(d).format("YYYY-MM-DD");
          return normalized.some((r) => iso >= r.start && iso <= r.end);
        },
        tooltip: (d: Date) => (tooltipPrefix ? `${tooltipPrefix} ${dayjs(d).format("DD/MM/YYYY")}` : undefined),
        label,
        variant,
        style,
        priority,
        marker
      };
    }
  },
  Uncontrolled: {
    /**
     * TransferList component in uncontrolled mode.
     * Use this version when you want to manage state manually without react-hook-form.
     *
     * @example
     * import { Input } from '@/components/@custom/Input';
     *
     * function MyComponent() {
     *   const [data, setData] = useState({
     *     left: ['Item 1', 'Item 2'],
     *     right: []
     *   });
     *
     *   return (
     *     <Input.Uncontrolled.TransferList
     *       value={data}
     *       onChange={setData}
     *       showTransferAll
     *     />
     *   );
     * }
     */
    TransferList: InputTransferList,
    Select: InputSelect,
    Number: InputNumber,
    Color: InputColor,
    Date: InputDate,
    DateRange: InputDateRange,
    Text: InputText,
    Pin: InputPin,
    Time: InputTime,
    Switch: InputSwitch,
    Password: InputPassword,
    Checkbox: InputCheckbox,
    RichText: (props: any) => (
      <Suspense fallback={null}>
        <LazyRichTextEditor {...props} />
      </Suspense>
    )
  }
};
